import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, ModalSize } from "@/shared/components/ui/Modal";
import { Button, ButtonSize, ButtonVariant } from "@/shared/ui/Button";

interface SendFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: { comment: string; attachments: File[] }) => Promise<void> | void;
}

const ATTACHMENT_FIELDS = [1, 2, 3] as const;

const SendFeedbackModal: React.FC<SendFeedbackModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState("");
  const [attachments, setAttachments] = useState<Array<File | null>>([null, null, null]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commentError, setCommentError] = useState("");
  const fileInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const resetForm = () => {
    setComment("");
    setAttachments([null, null, null]);
    setCommentError("");
    fileInputRefs.current.forEach((input) => {
      if (input) {
        input.value = "";
      }
    });
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onClose();
  };

  const handleSelectFile = (index: number) => {
    fileInputRefs.current[index]?.click();
  };

  const handleFileChange = (index: number, file?: File) => {
    setAttachments((prev) => {
      const next = [...prev];
      next[index] = file ?? null;
      return next;
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmedComment = comment.trim();

    if (!trimmedComment) {
      setCommentError(t("common.requiredFields"));
      return;
    }

    setCommentError("");
    setIsSubmitting(true);

    try {
      await onSubmit({
        comment: trimmedComment,
        attachments: attachments.filter((file): file is File => Boolean(file)),
      });
      resetForm();
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size={ModalSize.Xl}
      title={t("navbar.user.sendFeedback")}
      data-testid="send-feedback-modal"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="feedback-comment" className="block text-sm font-medium text-text mb-2">
            {t("common.comment")} <span className="text-error">*</span>
          </label>
          <textarea
            id="feedback-comment"
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
              if (commentError) {
                setCommentError("");
              }
            }}
            className="block w-full min-h-[120px] border border-border rounded-[4px] py-[8px] px-[10px] text-[14px] text-text bg-surface placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            data-testid="feedback-comment-input"
          />
          {commentError && <p className="text-xs text-error mt-1">{commentError}</p>}
        </div>

        {ATTACHMENT_FIELDS.map((fieldNumber, index) => (
          <div key={fieldNumber}>
            <label className="block text-sm font-medium text-text mb-2">
              {t("common.attachment")} {fieldNumber}
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={attachments[index]?.name ?? t("common.selectFile")}
                readOnly
                className="block w-full h-[36px] border border-border rounded-[4px] py-[6px] px-[10px] text-[14px] text-text bg-surface-alt"
                data-testid={`feedback-attachment-display-${fieldNumber}`}
              />
              <input
                ref={(el) => {
                  fileInputRefs.current[index] = el;
                }}
                type="file"
                className="hidden"
                onChange={(e) => handleFileChange(index, e.target.files?.[0])}
                data-testid={`feedback-attachment-input-${fieldNumber}`}
              />
              <Button
                type="button"
                variant={ButtonVariant.Primary}
                size={ButtonSize.Sm}
                onClick={() => handleSelectFile(index)}
                data-testid={`feedback-attachment-select-${fieldNumber}`}
              >
                {t("common.select")}
              </Button>
            </div>
          </div>
        ))}

        <div className="flex items-center gap-3 pt-2">
          <Button
            type="submit"
            variant={ButtonVariant.Primary}
            size={ButtonSize.Sm}
            disabled={isSubmitting}
            data-testid="feedback-submit-button"
          >
            {isSubmitting ? t("common.saving") : t("navbar.user.sendFeedback")}
          </Button>
          <Button
            type="button"
            variant={ButtonVariant.Outline}
            size={ButtonSize.Sm}
            onClick={handleClose}
            disabled={isSubmitting}
            data-testid="feedback-cancel-button"
          >
            {t("global.cancel")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SendFeedbackModal;